import { Component, HostBinding, inject, input, signal, ViewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import {
  ChangeSourceType,
  LookupKeys,
  Messages,
  MMDDYYYY_DATE_FORMAT,
  Policy,
  POLICY_TYPES_LOOKUP,
  PolicyKeys,
  PolicySigners,
  PolicySignersKeys,
  PolicyTypes,
  UserAgreementManagementKeys,
} from '@ag-common-lib/public-api';
import { DxDataGridComponent } from 'devextreme-angular';
import { BehaviorSubject, combineLatest, distinctUntilChanged, from, map, of, shareReplay, switchMap } from 'rxjs';
import {
  PolicyService,
  PolicySignersService,
  UserAgreementsManagementService,
  UserAgreementsPdfDownloadService,
} from 'ag-common-svc/lib/services/user-agreements';
import {
  getLastSignedAgreements,
  getMaxVersion,
} from 'ag-common-svc/lib/utils/last-signing-versions-by-agreement-type.util';
import { take } from 'rxjs/operators';
import { WasabiFile } from 'ag-common-svc/shared/pipes/wasabi-file.pipe';

@Component({
  selector: 'ag-shr-user-agreements-grid',
  templateUrl: './user-agreements-grid.component.html',
  styleUrls: ['./user-agreements-grid.component.scss'],
  providers: [UserAgreementsPdfDownloadService, WasabiFile],
})
export class UserAgreementsGridComponent {
  @HostBinding('class') protected className = 'user-agreements-grid';
  @ViewChild('userAgreementsGridRef', { static: false }) userAgreementsGridComponent: DxDataGridComponent;

  agentId = input<string>(null);
  isEditable = input<boolean>(true);
  canEdit = input<boolean>(true);
  canDelete = input<boolean>(true);
  canCreate = input<boolean>(true);
  sourceType = input<ChangeSourceType | null>(null);
  loadingPdfSig = signal<boolean>(false);

  private policySignersService = inject(PolicySignersService);
  private policyService = inject(PolicyService);
  private userAgreementsManagementService = inject(UserAgreementsManagementService);
  private wasabiFile = inject(WasabiFile);
  private pdfDownloadService = inject(UserAgreementsPdfDownloadService);
  private readonly agentId$ = toObservable(this.agentId);
  private readonly supportedPolicyTypes = Object.values(PolicyTypes);

  protected readonly emptyMessage = 'No User Agreements Currently Exist';
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly Messages = Messages;
  protected readonly PolicySignersKeys = PolicySignersKeys;
  protected readonly dateFormat = MMDDYYYY_DATE_FORMAT;
  protected readonly LookupKeys = LookupKeys;
  protected readonly POLICY_TYPES_LOOKUP = POLICY_TYPES_LOOKUP;
  protected readonly PolicyKeys = PolicyKeys;

  currentUserAgreements$ = combineLatest(
    this.supportedPolicyTypes.map(policyType =>
      this.policyService
        .getCurrentPublishedDocumentByType(policyType)
        .pipe(map(policies => ({ policyType, version: this.getLatestPublishedVersion(policies) }))),
    ),
  ).pipe(
    map(entries =>
      entries.reduce<Record<PolicyTypes, string | null>>(
        (acc, { policyType, version }) => {
          acc[policyType] = version;
          return acc;
        },
        {} as Record<PolicyTypes, string | null>,
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private lastSignedUserAgreements$ = this.agentId$.pipe(
    distinctUntilChanged(),
    switchMap(agentDbId => (agentDbId ? this.policySignersService.getSignersByAgentDbId(agentDbId) : of([]))),
    map(signers => this.buildGridRows(signers)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  lastSignedUserAgreementsSig = toSignal(this.lastSignedUserAgreements$, { initialValue: [] });
  readonly inProgress$ = new BehaviorSubject<boolean>(false);

  onInitNewRow = e => {
    e.data.is_active = !this.lastSignedUserAgreementsSig()?.length;
  };

  downloadPdf = data => {
    const policyDbId = data?.[PolicySignersKeys.policyDbId];
    if (!policyDbId) {
      console.warn('No policyDbId found in data');
      return;
    }

    const policyType = data?.[PolicySignersKeys.policyType];
    const policyVersion = data?.[PolicySignersKeys.policyVersion];

    // Get Policy to extract subversions
    const subversions$ = from(this.policyService.getById(policyDbId)).pipe(
      switchMap(policy => {
        const subversionsDbIds = policy?.[PolicyKeys.subversions];
        if (!subversionsDbIds || subversionsDbIds.length === 0) {
          console.warn('No subversions found in policy');
          return of([]);
        }
        // Get UserAgreementManagement objects from subversion DB IDs
        return this.userAgreementsManagementService.getSubversionByDbIds(subversionsDbIds);
      }),
      map(subversions => subversions
        .filter(sv => !!sv[UserAgreementManagementKeys.sourceFileKey])
        .map(sv => ({
          sourceFileKey: this.wasabiFile.transform(sv[UserAgreementManagementKeys.sourceFileKey]),
          agreementType: sv[UserAgreementManagementKeys.agreementType],
          language: sv[UserAgreementManagementKeys.language],
          fileVersion: sv[UserAgreementManagementKeys.fileVersion],
          masterVersion: policyVersion,
        }))
      )
    );

    const timeStamp = new Date().toJSON().slice(0, 19);
    const fileName = `${policyType}_${policyVersion}_${timeStamp}.zip`;

    this.pdfDownloadService
      .downloadPdfZip(subversions$, {
        fileName,
        onLoadingChange: loading => this.loadingPdfSig.set(loading),
      })
      .subscribe({
        error: err => {
          console.error('Error in downloadPdf stream:', err);
          this.loadingPdfSig.set(false);
        },
      });
  };

  private normalizeUserAgreements = (data: Partial<PolicySigners>, key?: PolicySigners): PolicySigners[] => {
    const agreements = [...(this.lastSignedUserAgreementsSig() ?? [])];
    if (!key) {
      return agreements;
    }
    return agreements.map(agreement =>
      agreement?.[BaseModelKeys.dbId] === key?.[BaseModelKeys.dbId]
        ? ({ ...agreement, ...data } as PolicySigners)
        : agreement,
    );
  };

  private updateUserAgreements = (userAgreements: PolicySigners[]) => {
    if (!userAgreements?.length) {
      return false;
    }

    this.inProgress$.next(true);
    const agentId = this.agentId();
    const sourceType = this.sourceType() ?? ChangeSourceType.regularArm;

    const updates = userAgreements
      .map(agreement => {
        const policyDbId = agreement?.[PolicySignersKeys.policyDbId];
        const signerDbId = agreement?.[BaseModelKeys.dbId];
        if (!policyDbId || !signerDbId) {
          return null;
        }
        const payload: Partial<PolicySigners> = {
          [PolicySignersKeys.policyVersion]: agreement[PolicySignersKeys.policyVersion],
          [PolicySignersKeys.agentDbId]: agreement[PolicySignersKeys.agentDbId] ?? agentId,
          [PolicySignersKeys.policyType]: agreement[PolicySignersKeys.policyType],
        };
        return this.policySignersService.updatePolicySigners(policyDbId, signerDbId, payload, sourceType);
      })
      .filter(Boolean);

    return Promise.all(updates)
      .catch(error => {
        console.error('Failed to update policy signers', error);
      })
      .finally(() => {
        this.inProgress$.next(false);
      });
  };

  private buildGridRows(signers: PolicySigners[]): PolicySigners[] {
    console.log('buildGridRows signers', signers);
    const latestByType = getLastSignedAgreements(signers ?? []);
    const latestByTypeMap = new Map<PolicyTypes, PolicySigners>(
      latestByType.map(item => [item?.[PolicySignersKeys.policyType], item]),
    );
    return this.supportedPolicyTypes.map(policyType => {
      const existing = latestByTypeMap.get(policyType);
      if (existing) {
        return existing;
      }
      return {
        [BaseModelKeys.dbId]: `${policyType}-placeholder`,
        [PolicySignersKeys.policyType]: policyType,
        [PolicySignersKeys.agentDbId]: this.agentId(),
        [PolicySignersKeys.agentId]: '',
        [PolicySignersKeys.agentEmail]: '',
        [PolicySignersKeys.agentFirstName]: '',
        [PolicySignersKeys.agentLastName]: '',
        [PolicySignersKeys.agentMgaId]: '',
        [PolicySignersKeys.policyDbId]: '',
        [PolicySignersKeys.policyVersion]: null,
        [BaseModelKeys.updatedDate]: null,
      } as PolicySigners;
    });
  }

  private getLatestPublishedVersion(policies: Policy[] = []): string | null {
    const versions = (policies ?? [])
      .map(policy => policy?.[PolicyKeys.version])
      .filter((version): version is string => !!version);
    if (!versions.length) {
      return null;
    }
    return getMaxVersion(versions) ?? null;
  }
}
